/**
 * @fileoverview Autodesk Forge Viewer component for displaying Revit models.
 * Handles 3D/2D view switching, translation status polling, resize behavior,
 * and local derivative storage mode for EU data compliance.
 */

import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import {
	clearModelViewerInit,
	getForgePublicToken,
	getModelData,
	getModelTranslationStatus,
	getModelViewerInit,
	updateModelThumbnail,
	updateViewerViewInfo,
} from "../../../../../actions";
import { setViewerInstance } from "../../../../../hooks/useViewer";

// Side-effect import: registers CustomMarkupExtension with Autodesk
import "./markup-extension";

// Constants and utilities
import {
	VIEW_TYPE,
	VIEWER_MODE,
	VIEWER_THEME,
	TRANSLATION_STATUS,
	TRANSLATION_POLL_INTERVAL_MS,
	VIEWER_EXTENSIONS,
} from "./viewer-constants";
import {
	createTranslatingMessageHTML,
	fitModelToView,
	updateMarkupExtensionView,
	cleanupViewer,
} from "./viewer-utils";

// ============================================================================
// Types
// ============================================================================

/**
 * @typedef {Object} DocData
 * @property {any} [doc] - Autodesk document instance
 * @property {any[]} sheets - Array of 2D sheet viewables
 * @property {any} viewable - Default 3D viewable
 * @property {number} currentViewIndex - Index of current 2D sheet (-1 for 3D)
 * @property {string} currentViewType - Current view type (VIEW_TYPE.THREE_D or VIEW_TYPE.TWO_D)
 */

/**
 * @typedef {Object} ForgeAuth
 * @property {string} access_token - Forge API access token
 */

/**
 * @typedef {Object} ModelViewerInitData
 * @property {string} viewerType - "local" or "aps"
 * @property {string} [svfUrl] - Direct public URL to SVF file in DigitalOcean Spaces (local mode)
 * @property {string} [manifestUrl] - URL to derivative-manifest.json (local mode)
 * @property {string} [bubbleUrl] - URL to original APS manifest.json (local mode)
 * @property {string} [baseUrl] - Base URL for resolving derivative assets (local mode)
 * @property {string} [urn] - Forge URN for APS mode
 * @property {string} [transferStatus] - Status of derivative transfer
 */

/**
 * @typedef {Object} RevitForgeViewerProps
 * @property {boolean} [enableResize=true] - Enable resize observer for responsive behavior
 * @property {ForgeAuth} [forgeAuth] - Forge authentication data from Redux
 * @property {string} viewerUrn - URN of the model to display
 * @property {number} [fileId] - File entity ID for model viewer init
 * @property {Function} getForgePublicToken - Action to fetch Forge token
 * @property {Function} getModelTranslationStatus - Action to check translation status
 * @property {Function} getModelData - Action to fetch model metadata
 * @property {Function} updateViewerViewInfo - Action to update current view in Redux
 * @property {Function} getModelViewerInit - Action to get viewer init data
 * @property {Function} clearModelViewerInit - Action to clear viewer init data
 */

// ============================================================================
// Helpers
// ============================================================================

/**
 * Creates the initial docData state.
 * Used as lazy initializer to avoid recreating on every render.
 * @returns {DocData}
 */
const createInitialDocData = () => ({
	sheets: [],
	viewable: null,
	currentViewIndex: -1,
	currentViewType: VIEW_TYPE.THREE_D,
});

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Memoized view controls component for 3D/2D switching.
 * Extracted to prevent unnecessary re-renders of control buttons.
 */
const ViewerControls = memo(function ViewerControls({
	viewable,
	sheets,
	currentViewType,
	currentViewIndex,
	isViewerReady,
	onLoadView,
}) {
	// Don't show controls if no views available
	if (!viewable && sheets.length === 0) {
		return null;
	}

	return (
		<div className="viewer--controls">
			{/* 3D View Button */}
			{viewable && (
				<button
					className={`viewer--controls--button ${currentViewType === VIEW_TYPE.THREE_D ? "active" : ""}`}
					onClick={() => onLoadView(viewable, VIEW_TYPE.THREE_D, -1)}
					disabled={!isViewerReady}
				>
					3D View
				</button>
			)}

			{/* 2D Sheet Buttons */}
			{sheets.map((sheet, index) => {
				const sheetName = sheet?.data?.name || `Sheet ${index + 1}`;
				const sheetGuid = sheet?.data?.guid || sheet?.guid;
				const uniqueKey =
					sheetGuid && typeof sheetGuid === "string"
						? sheetGuid
						: `sheet-${index}`;

				return (
					<button
						key={uniqueKey}
						className={`viewer--controls--button ${currentViewIndex === index ? "active" : ""}`}
						onClick={() => onLoadView(sheet, VIEW_TYPE.TWO_D, index)}
						disabled={!isViewerReady}
					>
						{sheetName}
					</button>
				);
			})}
		</div>
	);
});

ViewerControls.propTypes = {
	viewable: PropTypes.object,
	sheets: PropTypes.array.isRequired,
	currentViewType: PropTypes.string.isRequired,
	currentViewIndex: PropTypes.number.isRequired,
	isViewerReady: PropTypes.bool.isRequired,
	onLoadView: PropTypes.func.isRequired,
};

// ============================================================================
// Component
// ============================================================================

/**
 * Autodesk Forge Viewer component for displaying Revit 3D models and 2D sheets.
 *
 * Features:
 * - Automatic translation status polling for models being processed
 * - 3D/2D view switching with sheet selection
 * - Responsive resize handling
 * - Integration with CustomMarkupExtension for annotations
 * - Local derivative storage mode for EU data compliance
 *
 * @param {RevitForgeViewerProps} props - Component props
 * @returns {JSX.Element} Viewer container with controls
 */
const RevitForgeViewer = (props) => {
	const {
		enableResize = true,
		forgeAuth,
		viewerUrn,
		fileId,
		// Actions
		getForgePublicToken,
		getModelTranslationStatus,
		getModelData,
		updateViewerViewInfo,
		getModelViewerInit,
		clearModelViewerInit,
	} = props;

	// -------------------------------------------------------------------------
	// Refs
	// -------------------------------------------------------------------------

	const viewerWrapperRef = useRef(null);
	const viewerRef = useRef(null);
	/** Stores document instance for use in loadView callback (avoids docData dependency) */
	const docRef = useRef(null);
	const translationPollTimeoutRef = useRef(null);
	const translationPollCountRef = useRef(0);
	const originalGetItemApiRef = useRef(null);
	const currentUrnRef = useRef(viewerUrn);

	// -------------------------------------------------------------------------
	// State
	// -------------------------------------------------------------------------

	const [isViewerReady, setIsViewerReady] = useState(false);
	const [viewerInitData, setViewerInitData] = useState(null);
	/** @type {[DocData, Function]} */
	const [docData, setDocData] = useState(createInitialDocData);

	// -------------------------------------------------------------------------
	// Memoized Values
	// -------------------------------------------------------------------------

	/** Determine which viewer mode to use */
	const isLocalMode = viewerInitData?.viewerType === VIEWER_MODE.LOCAL;

	/** Forge initialization options - regenerates when mode or token changes */
	const forgeOptions = useMemo(() => {
		if (isLocalMode) {
			return {
				env: "Local",
			};
		}

		return forgeAuth?.access_token
			? {
					env: "AutodeskProduction",
					api: "derivativeV2_EU",
					accessToken: forgeAuth.access_token,
				}
			: null;
	}, [isLocalMode, forgeAuth?.access_token]);

	/** Viewer configuration with extensions */
	const viewerConfig = useMemo(
		() => ({
			extensions: VIEWER_EXTENSIONS,
		}),
		[]
	);

	// -------------------------------------------------------------------------
	// Callbacks
	// -------------------------------------------------------------------------

	/**
	 * Loads a specific view (3D or 2D sheet) into the viewer.
	 * Uses functional setState and refs to maintain stable callback identity.
	 * @param {any} viewableNode - The viewable node to load
	 * @param {string} viewType - View type (VIEW_TYPE.THREE_D or VIEW_TYPE.TWO_D)
	 * @param {number} viewIndex - Index of the view (-1 for 3D)
	 */
	const loadView = useCallback(
		(viewableNode, viewType, viewIndex) => {
			const viewer = viewerRef.current;
			const doc = docRef.current;

			if (!viewer) {
				console.warn("loadView called before viewer initialization");
				return;
			}

			if (!doc) {
				console.warn("loadView called before document loaded");
				return;
			}

			viewer
				.loadDocumentNode(doc, viewableNode)
				.then(() => {
					// Use functional setState to avoid stale closure
					setDocData((prev) => ({
						...prev,
						currentViewType: viewType,
						currentViewIndex: viewIndex,
					}));

					updateViewerViewInfo({ viewType, viewIndex });

					setViewerInstance(viewer);

					getModelData(viewerUrn);

					// Sync markup extension with current view
					updateMarkupExtensionView(viewer, viewType, viewIndex);

					// Center 3D models in viewport
					fitModelToView(viewer, "after loading view");
				})
				.catch((err) => {
					console.error("Error loading view:", err);
				});
		},
		[updateViewerViewInfo, getModelData, viewerUrn]
	);

	/**
	 * Handles successful document load from Forge.
	 * Extracts sheets and default viewable, then loads the initial view.
	 * @param {any} doc - The loaded Autodesk document
	 */
	const handleDocumentLoadSuccess = useCallback(
		(doc) => {
			const root = doc.getRoot();
			const sheets = root.search({ type: "geometry", role: "2d" }, true);
			const viewable = root.getDefaultGeometry();

			// Store document reference for use in loadView callback
			docRef.current = doc;

			const newDocData = {
				doc,
				sheets,
				viewable,
				currentViewIndex: -1,
				currentViewType: VIEW_TYPE.THREE_D,
			};
			setDocData(newDocData);

			// Load 3D view if available, otherwise fall back to first 2D sheet
			if (viewable && viewerRef.current) {
				loadView(viewable, VIEW_TYPE.THREE_D, -1);
			} else if (sheets.length > 0) {
				loadView(sheets[0], VIEW_TYPE.TWO_D, 0);
			}
		},
		[loadView]
	);

	/**
	 * Clears the translation polling timeout if active.
	 */
	const clearTranslationPollTimeout = useCallback(() => {
		if (translationPollTimeoutRef.current) {
			clearTimeout(translationPollTimeoutRef.current);
			translationPollTimeoutRef.current = null;
		}
	}, []);

	/**
	 * Initializes the viewer in local mode using the APS bubble/manifest from DigitalOcean Spaces.
	 * This approach mirrors APS mode by loading a Document, enabling 2D/3D view switching.
	 * @param {HTMLElement} containerElement - DOM element to render viewer into
	 * @param {ModelViewerInitData} initData - Viewer initialization data with URLs
	 */
	const initializeLocalViewer = useCallback(
		async (containerElement, initData) => {
			if (viewerRef.current) {
				return;
			}

			const { bubbleUrl, baseUrl } = initData;
			if (!bubbleUrl || !baseUrl) {
				console.error("Local viewer requires bubbleUrl and baseUrl");
				return;
			}

			try {
				// Fetch bubble manifest from DigitalOcean Spaces
				const response = await fetch(bubbleUrl);
				if (!response.ok) {
					throw new Error(`Failed to fetch bubble: ${response.status}`);
				}
				const bubbleData = await response.json();

				const localOptions = { env: "Local" };

				Autodesk.Viewing.Initializer(localOptions, () => {
					if (!viewerWrapperRef.current || viewerRef.current) return;

					// Clear any cached APS authorization header to prevent CORS errors
					// when loading assets from DigitalOcean Spaces. The APS token would
					// otherwise be sent to DO requests, causing them to fail.
					if (Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS) {
						delete Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS.Authorization;
					}

					// Set up endpoint for local derivative loading
					Autodesk.Viewing.endpoint.setEndpointAndApi(baseUrl, "");

					// Override getItemApi to resolve derivative paths correctly
					const originalGetItemApi = Autodesk.Viewing.endpoint.getItemApi;
					originalGetItemApiRef.current = originalGetItemApi;
					Autodesk.Viewing.endpoint.getItemApi = (
						endpoint,
						derivativeUrn,
						api
					) => {
						if (!derivativeUrn) {
							return originalGetItemApi.call(
								Autodesk.Viewing.endpoint,
								endpoint,
								derivativeUrn,
								api
							);
						}
						const decodedUrn = decodeURIComponent(derivativeUrn);
						const resourceMatch = decodedUrn.match(/output\/(.+)$/);
						if (resourceMatch) {
							return `${baseUrl}/${resourceMatch[1]}`;
						}
						return `${baseUrl}/${decodedUrn}`;
					};

					// Create and configure viewer instance
					const newViewer = new Autodesk.Viewing.GuiViewer3D(
						containerElement,
						viewerConfig
					);
					newViewer.start();

					if (typeof newViewer.resize === "function") {
						newViewer.resize();
					}

					setViewerInstance(newViewer);
					setIsViewerReady(true);

					if (typeof newViewer.setTheme === "function") {
						newViewer.setTheme(VIEWER_THEME);
					}

					viewerRef.current = newViewer;

					// Load document from bubble data
					const doc = new Autodesk.Viewing.Document(bubbleData);
					handleDocumentLoadSuccess(doc);
				});
			} catch (err) {
				console.error("Error initializing local viewer:", err);
			}
		},
		[viewerConfig, handleDocumentLoadSuccess]
	);

	/**
	 * Initializes the Forge viewer in APS mode and loads the model.
	 * Handles translation status polling for models still being processed.
	 * @param {HTMLElement} containerElement - DOM element to render viewer into
	 */
	const initializeApsViewer = useCallback(
		async (containerElement) => {
			if (!viewerUrn || !forgeAuth?.access_token) {
				return;
			}

			const viewer = viewerRef.current;
			if (viewer) return;

			try {
				// Check translation status
				const status = await getModelTranslationStatus(
					viewerUrn,
					forgeAuth.access_token
				);

				const isTranslationComplete =
					status.status === TRANSLATION_STATUS.COMPLETED ||
					status.status === TRANSLATION_STATUS.SUCCESS;

				// If translation still in progress, poll with exponential backoff
				if (!isTranslationComplete) {
					const progress = status.success || status.progress || "0";
					containerElement.innerHTML = createTranslatingMessageHTML(progress);
					clearTranslationPollTimeout();

					translationPollCountRef.current += 1;
					const backoffInterval = Math.min(
						TRANSLATION_POLL_INTERVAL_MS *
							Math.pow(1.5, translationPollCountRef.current - 1),
						30000 // Max 30 seconds
					);

					translationPollTimeoutRef.current = setTimeout(
						() => initializeApsViewer(containerElement),
						backoffInterval
					);
					return;
				}

				// Translation complete - create viewer
				clearTranslationPollTimeout();
				translationPollCountRef.current = 0;

				if (!viewerRef.current) {
					const newViewer = new Autodesk.Viewing.GuiViewer3D(
						containerElement,
						viewerConfig
					);
					newViewer.start();

					if (typeof newViewer.resize === "function") {
						newViewer.resize();
					}

					setViewerInstance(newViewer);
					setIsViewerReady(true);

					if (typeof newViewer.setTheme === "function") {
						newViewer.setTheme(VIEWER_THEME);
					}

					viewerRef.current = newViewer;

					// Load document from APS
					Autodesk.Viewing.Document.load(
						`urn:${viewerUrn}`,
						handleDocumentLoadSuccess,
						(errorCode, errorMessage) => {
							console.error(`Forge document load failed: ${errorMessage}`, {
								errorCode,
								viewerUrn,
							});
						}
					);
				}
			} catch (err) {
				console.error("Error initializing APS viewer:", err);
			}
		},
		[
			viewerUrn,
			forgeAuth?.access_token,
			getModelTranslationStatus,
			clearTranslationPollTimeout,
			viewerConfig,
			handleDocumentLoadSuccess,
		]
	);

	/**
	 * Creates and manages a ResizeObserver for responsive viewer behavior.
	 * @returns {ResizeObserver|null} The observer instance or null if unsupported
	 */
	const createResizeObserver = useCallback(() => {
		if (typeof ResizeObserver === "undefined") {
			console.warn("ResizeObserver is not supported in this browser.");
			return null;
		}

		const wrapper = viewerWrapperRef.current;
		if (!wrapper) return null;

		let lastWidth = wrapper.clientWidth ?? 0;

		return new ResizeObserver((entries) => {
			const viewer = viewerRef.current;
			if (!viewer) return;

			viewer.resize();

			// Re-center 3D model only when width changes (avoid unnecessary recalculations)
			for (const entry of entries) {
				const newWidth = Math.round(
					entry.contentRect?.width ?? wrapper.clientWidth ?? 0
				);

				if (newWidth !== lastWidth) {
					lastWidth = newWidth;
					fitModelToView(viewer, "after width change");
				}
			}
		});
	}, []);

	// -------------------------------------------------------------------------
	// Effects
	// -------------------------------------------------------------------------

	// Fetch model viewer init data when fileId is available
	useEffect(() => {
		if (!fileId) {
			return;
		}

		let isMounted = true;
		let didSetData = false;

		getModelViewerInit(fileId)
			.then((data) => {
				if (!isMounted) return;

				if (data) {
					setViewerInitData(data);
					didSetData = true;
				} else {
					console.warn(
						"getModelViewerInit returned null/undefined for fileId:",
						fileId
					);
				}
			})
			.catch((err) => {
				if (!isMounted) return;

				console.error("Failed to get model viewer init data:", err);
			});

		return () => {
			isMounted = false;
			if (didSetData) {
				clearModelViewerInit();
			}
		};
	}, [fileId, getModelViewerInit, clearModelViewerInit]);

	// Fetch Forge token on mount (needed for APS mode)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		getForgePublicToken();
	}, []); // Intentionally empty - only fetch once on mount

	// Restore endpoint proxy before re-initializing (must happen BEFORE initialization)
	useEffect(() => {
		if (originalGetItemApiRef.current) {
			Autodesk.Viewing.endpoint.getItemApi = originalGetItemApiRef.current;
			originalGetItemApiRef.current = null;
		}
	}, [viewerInitData?.viewerType]);

	// Initialize viewer based on mode
	useEffect(() => {
		if (!viewerWrapperRef.current) {
			return;
		}

		if (viewerRef.current && viewerUrn !== currentUrnRef.current) {
			cleanupViewer(viewerRef);
			setViewerInstance(null);
			setIsViewerReady(false);
		}

		// Update current URN tracking
		currentUrnRef.current = viewerUrn;

		if (isLocalMode && viewerInitData?.bubbleUrl && viewerInitData?.baseUrl) {
			initializeLocalViewer(viewerWrapperRef.current, viewerInitData);
		} else if (!isLocalMode && forgeOptions && viewerUrn) {
			Autodesk.Viewing.Initializer(forgeOptions, () => {
				if (viewerWrapperRef.current) {
					initializeApsViewer(viewerWrapperRef.current);
				}
			});
		}

		// Cleanup: teardown viewer when unmounting
		return () => {
			clearTranslationPollTimeout();
			cleanupViewer(viewerRef);
			// Clear document reference
			docRef.current = null;
			// Restore original endpoint proxy to prevent CORS errors on navigation
			if (originalGetItemApiRef.current) {
				Autodesk.Viewing.endpoint.getItemApi = originalGetItemApiRef.current;
				originalGetItemApiRef.current = null;
			}
			// Clear global viewer instance to prevent stale references across navigation
			setViewerInstance(null);
			setIsViewerReady(false);
			setDocData(createInitialDocData());
			translationPollCountRef.current = 0;
		};
	}, [
		viewerInitData,
		isLocalMode,
		forgeOptions,
		viewerUrn,
		initializeLocalViewer,
		initializeApsViewer,
		clearTranslationPollTimeout,
	]);

	// Setup resize observer when viewer is ready
	useEffect(() => {
		if (
			!enableResize ||
			!isViewerReady ||
			!viewerRef.current ||
			!viewerWrapperRef.current
		) {
			return;
		}

		// Perform initial resize and fit
		viewerRef.current.resize();
		fitModelToView(viewerRef.current, "initial resize");

		// Setup continuous resize observation
		const resizeObserver = createResizeObserver();
		if (resizeObserver) {
			resizeObserver.observe(viewerWrapperRef.current);
		}

		return () => {
			resizeObserver?.disconnect();
		};
	}, [enableResize, isViewerReady, createResizeObserver]);

	// -------------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------------

	return (
		<div className="viewer-container">
			<div className="viewer--forge" ref={viewerWrapperRef} />
			<ViewerControls
				viewable={docData.viewable}
				sheets={docData.sheets}
				currentViewType={docData.currentViewType}
				currentViewIndex={docData.currentViewIndex}
				isViewerReady={isViewerReady}
				onLoadView={loadView}
			/>
		</div>
	);
};

// ============================================================================
// PropTypes
// ============================================================================

RevitForgeViewer.propTypes = {
	/** Enable resize observer for responsive behavior */
	enableResize: PropTypes.bool,
	/** Forge authentication data from Redux */
	forgeAuth: PropTypes.shape({
		access_token: PropTypes.string,
	}),
	/** URN of the model to display */
	viewerUrn: PropTypes.string.isRequired,
	/** File entity ID for model viewer init */
	fileId: PropTypes.number,
	/** Action to fetch Forge token */
	getForgePublicToken: PropTypes.func.isRequired,
	/** Action to check translation status */
	getModelTranslationStatus: PropTypes.func.isRequired,
	/** Action to fetch model metadata */
	getModelData: PropTypes.func.isRequired,
	/** Action to update current view in Redux */
	updateViewerViewInfo: PropTypes.func.isRequired,
	/** Action to get viewer init data */
	getModelViewerInit: PropTypes.func.isRequired,
	/** Action to clear viewer init data */
	clearModelViewerInit: PropTypes.func.isRequired,
};

RevitForgeViewer.defaultProps = {
	enableResize: true,
	forgeAuth: null,
	fileId: null,
};

// ============================================================================
// Redux Connection
// ============================================================================

/**
 * Maps Redux state to component props.
 * @param {import("immutable").Map<string, any>} state - Immutable Redux state
 */
const mapStateToProps = (state, ownProps) => ({
	/** Forge authentication data (token, expiry) */
	forgeAuth: state.getIn(["building", "building", "forgeAuth"]),

	/** Current user's profile image URL */
	userImageLink: state.getIn(["authentication", "user", "imageLink"]),

	/** Current user's full name (for display purposes) */
	userName:
		state.getIn(["authentication", "user", "firstName"]) &&
		state.getIn(["authentication", "user", "lastName"])
			? `${state.getIn(["authentication", "user", "firstName"])} ${state.getIn(["authentication", "user", "lastName"])}`
			: null,

	/** ID of the currently viewed building discipline file */
	buildingDisciplineFileId: state.getIn(["building", "buildingViewer", "id"]),

	/** File entity ID for model viewer init - use prop if passed (for packages), otherwise from state (for buildings) */
	fileId:
		ownProps.fileId ??
		state.getIn(["building", "buildingViewer", "file", "id"]),
});

/** Actions to bind to dispatch */
const mapDispatchToProps = {
	getForgePublicToken,
	getModelTranslationStatus,
	updateModelThumbnail,
	updateViewerViewInfo,
	getModelData,
	getModelViewerInit,
	clearModelViewerInit,
};

export default connect(mapStateToProps, mapDispatchToProps)(RevitForgeViewer);
